import { useEffect, useState } from 'react'
import { AlertCircle, RefreshCw, ArrowLeft, LayoutGrid } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@jira-clone/ui'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { useAuth } from '../context/AuthContext'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { useDashboardOverview } from '../features/dashboard/api'
import { WidgetSkeleton } from '../features/dashboard/components/WidgetFrame'
import { WidgetRenderer } from '../features/dashboard/widgetRegistry'
import type { WidgetSize } from '../features/dashboard/types'
import { useSection } from '../context/SectionContext'
import SectionCards from '../features/dashboard/components/SectionCards'
import WelcomeIllustration from '../features/dashboard/components/WelcomeIllustration'
import { type SectionKey } from '../features/dashboard/sections'
import { DashboardPeriodProvider } from '../features/dashboard/period'
import { PeriodPicker } from '../features/dashboard/components/PeriodPicker'

// Department-aware Overview. The backend (GET /dashboard/overview) decides
// which widgets this user gets, from their department and permissions; this
// page only lays them out. Each widget loads its own data, so one slow or
// failing widget doesn't hold up the rest.

// Static class names so Tailwind can see them.
const SIZE_CLASSES: Record<WidgetSize, string> = {
  sm: 'md:col-span-6 lg:col-span-3',
  md: 'lg:col-span-6',
  lg: 'lg:col-span-8',
  full: 'col-span-12',
}

// Layout-only overrides for the Company overview, mirroring the analytics dashboard's arrangement.
// Frontend CSS only: it changes how these cards sit on the grid, not their data, order or the other cards.
const COMPANY_SPAN: Record<string, string> = {
  'company-headline': 'col-span-12',
  'company-clients': 'md:col-span-6 lg:col-span-6',
  'company-sales': 'md:col-span-6 lg:col-span-6',
  'company-scoreboard': 'md:col-span-12 lg:col-span-9',
  'company-lead-sources': 'md:col-span-12 lg:col-span-3',
  'company-collections': 'md:col-span-6 lg:col-span-4',
  'company-delivery': 'md:col-span-6 lg:col-span-4',
  'company-projects': 'md:col-span-6 lg:col-span-4',
}

// Needs Your Attention and Meetings lead the page, followed by the eight reference
// cards in the mockup's rows; the remaining full-width panels (Money) flow below.
// lg-only, so smaller screens keep the natural stacking order.
const COMPANY_ORDER: Record<string, string> = {
  'company-actions': 'lg:order-1',
  'my-meetings-calendar': 'lg:order-2',
  'company-headline': 'lg:order-3',
  'company-clients': 'lg:order-4',
  'company-sales': 'lg:order-5',
  'company-scoreboard': 'lg:order-6',
  'company-lead-sources': 'lg:order-7',
  'company-collections': 'lg:order-8',
  'company-delivery': 'lg:order-9',
  'company-projects': 'lg:order-10',
}

export default function DashboardPage() {
  return (
    <DashboardPeriodProvider>
      <DashboardContent />
    </DashboardPeriodProvider>
  )
}

function DashboardContent() {
  const { organization, user } = useAuth()
  const { data, isLoading, error, refetch } = useDashboardOverview()
  const [activeTabId, setActiveTabId] = useState<string | undefined>()
  const { selectedSection, setSelectedSection } = useSection()

  const tabs = data?.tabs ?? []
  const profile = data?.profile

  const isSuperAdmin = isSuperAdminOrOwner(user?.role) || profile?.key === 'superadmin'
  const isDepartmentSelection = isSuperAdmin && !selectedSection

  // Filter tabs based on selected section (only for superadmin who has section cards)
  let filteredTabs = tabs
  if (isSuperAdmin && selectedSection && selectedSection !== 'company') {
    // Map section to backend tab ids
    const sectionTabMap: Record<string, string[]> = {
      sales: ['sales'],
      accounts: ['accounts'],
      people: ['hr'],
      hr: ['hr'],
      projects: ['projects', 'overview'],
    }
    const allowedTabIds = sectionTabMap[selectedSection] || []
    filteredTabs = tabs.filter((t) => allowedTabIds.includes(t.id))
  }

  // Synchronize active tab whenever selectedSection changes
  useEffect(() => {
    if (selectedSection && selectedSection !== 'company') {
      const sectionTabMap: Record<string, string> = {
        sales: 'sales',
        accounts: 'accounts',
        people: 'hr',
        hr: 'hr',
        projects: 'projects',
      }
      const targetTab = sectionTabMap[selectedSection]
      if (targetTab && tabs.some((t) => t.id === targetTab)) {
        setActiveTabId(targetTab)
      }
    } else {
      if (tabs.some((t) => t.id === 'company')) {
        setActiveTabId('company')
      } else if (tabs[0]) {
        setActiveTabId(tabs[0].id)
      }
    }
  }, [selectedSection, tabs])

  const activeTab = filteredTabs.find((t) => t.id === activeTabId) ?? filteredTabs[0]

  return (
    <div className="w-full space-y-7 sm:space-y-8">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-2xl border border-primary/15 bg-gradient-to-r from-primary/10 via-primary/[0.04] to-card shadow-panel px-6 py-7 sm:px-9 sm:py-8">
        <div className="pointer-events-none absolute -right-24 -bottom-32 h-80 w-80 rounded-full bg-primary/[0.07]" />
        <div className="pointer-events-none absolute -right-8 -top-12 h-44 w-44 rounded-full bg-primary/10 blur-2xl" />
        <div className="relative">
          {(profile?.department || isSuperAdmin) && (
            <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold bg-primary/10 text-primary border border-primary/20 hover:bg-primary/10">
              <LayoutGrid className="h-3 w-3" aria-hidden="true" />
              {profile?.department || 'Superadmin'}
              {profile?.departmentRole ? ` · ${profile.departmentRole}` : isSuperAdmin ? ' · Super Admin' : ''}
            </Badge>
          )}
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
            Welcome back, <span className="text-primary">{user?.name || user?.email?.split('@')[0]}</span>
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            {isSuperAdmin ? (
              <>
                Here is an overview of what is happening in{' '}
                <span className="font-semibold text-foreground">{organization?.name}</span> today.
              </>
            ) : (
              <>
                Here is your{' '}
                <span className="font-semibold text-foreground capitalize">
                  {profile?.department ? `${profile.department} ` : ''}Dashboard
                </span>{' '}
                overview for today.
              </>
            )}
          </p>
        </div>
        <WelcomeIllustration className="relative hidden md:block h-40 lg:h-52 w-auto shrink-0" />
      </div>

      {/* For SuperAdmin: The 5 cards are ONLY displayed at the time of sign in (when no section is selected) */}
      {isSuperAdmin && !selectedSection && (
        <div className="pt-2 animate-in fade-in-50 zoom-in-[0.99] duration-300">
          <SectionCards
            availableSections={['company', 'sales', 'accounts', 'projects', 'hr']}
            hideHeader
          />
        </div>
      )}

      {/* Overview widgets: displayed for regular users, or for superadmin after selecting a department card */}
      {(!isSuperAdmin || selectedSection) && (
        <div className="transition-all duration-300 animate-in fade-in-50">
          {isLoading ? (
            <div data-testid="overview-loading" className="grid gap-6 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <WidgetSkeleton />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card role="alert" className="border-destructive/40 bg-destructive/5">
              <CardContent className="flex items-center justify-between p-6">
                <div className="flex items-center gap-3 text-destructive">
                  <AlertCircle className="h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-medium">Failed to load overview</p>
                    <p className="text-xs text-muted-foreground">{getApiErrorMessage(error, 'Please try again.')}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
                </Button>
              </CardContent>
            </Card>
          ) : !activeTab ? (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                Nothing to show yet. Ask an administrator to set your department or access.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-12 gap-6 animate-fade-in">
              {/* The Company tab's money widgets follow a date range the viewer picks. */}
              {isSuperAdmin && activeTab.id === 'company' && (
                <div className="col-span-12">
                  <PeriodPicker />
                </div>
              )}
              {activeTab.widgets.map((widget) => {
                const isCompany = activeTab.id === 'company'
                const span = isCompany ? COMPANY_SPAN[widget.id] ?? SIZE_CLASSES[widget.size] ?? '' : SIZE_CLASSES[widget.size] ?? ''
                const order = isCompany ? COMPANY_ORDER[widget.id] ?? 'lg:order-last' : ''
                return (
                  <div key={widget.id} className={`col-span-12 h-full ${span} ${order}`}>
                    <WidgetRenderer widget={widget} />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
